import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Col, Form, FormGroup, Input, InputGroup, Row, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, Translate, getPaginationState, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities, searchEntities } from './user-lab-test-item.reducer';

export const UserLabTestItem = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const userLabTestItemList = useAppSelector(state => state.userLabTestItem.entities);
  const loading = useAppSelector(state => state.userLabTestItem.loading);
  const totalItems = useAppSelector(state => state.userLabTestItem.totalItems);

  const getAllEntities = () => {
    if (search) {
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    } else {
      dispatch(
        getEntities({
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
  };

  const startSearching = e => {
    if (search) {
      setPaginationState({
        ...paginationState,
        activePage: 1,
      });
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
    e.preventDefault();
  };

  const clear = () => {
    setSearch('');
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
    dispatch(getEntities({}));
  };

  const handleSearch = event => setSearch(event.target.value);

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort, search]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="user-lab-test-item-heading" data-cy="UserLabTestItemHeading">
        <Translate contentKey="tinyMedicRestApp.userLabTestItem.home.title">User Lab Test Items</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="tinyMedicRestApp.userLabTestItem.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link
            to="/user-lab-test-item/new"
            className="btn btn-primary jh-create-entity"
            id="jh-create-entity"
            data-cy="entityCreateButton"
          >
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="tinyMedicRestApp.userLabTestItem.home.createLabel">Create new User Lab Test Item</Translate>
          </Link>
        </div>
      </h2>
      <Row>
        <Col sm="12">
          <Form onSubmit={startSearching}>
            <FormGroup>
              <InputGroup>
                <Input
                  type="text"
                  name="search"
                  defaultValue={search}
                  onChange={handleSearch}
                  placeholder={translate('tinyMedicRestApp.userLabTestItem.home.search')}
                />
                <Button className="input-group-addon">
                  <FontAwesomeIcon icon="search" />
                </Button>
                <Button type="reset" className="input-group-addon" onClick={clear}>
                  <FontAwesomeIcon icon="trash" />
                </Button>
              </InputGroup>
            </FormGroup>
          </Form>
        </Col>
      </Row>
      <div className="table-responsive">
        {userLabTestItemList && userLabTestItemList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="tinyMedicRestApp.userLabTestItem.id">Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('active')}>
                  <Translate contentKey="tinyMedicRestApp.userLabTestItem.active">Active</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('active')} />
                </th>
                <th className="hand" onClick={sort('lastupdated')}>
                  <Translate contentKey="tinyMedicRestApp.userLabTestItem.lastupdated">Lastupdated</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('lastupdated')} />
                </th>
                <th className="hand" onClick={sort('microSec')}>
                  <Translate contentKey="tinyMedicRestApp.userLabTestItem.microSec">Micro Sec</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('microSec')} />
                </th>
                <th className="hand" onClick={sort('result')}>
                  <Translate contentKey="tinyMedicRestApp.userLabTestItem.result">Result</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('result')} />
                </th>
                <th className="hand" onClick={sort('timestampentry')}>
                  <Translate contentKey="tinyMedicRestApp.userLabTestItem.timestampentry">Timestampentry</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('timestampentry')} />
                </th>
                <th className="hand" onClick={sort('observation')}>
                  <Translate contentKey="tinyMedicRestApp.userLabTestItem.observation">Observation</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('observation')} />
                </th>
                <th className="hand" onClick={sort('cursor')}>
                  <Translate contentKey="tinyMedicRestApp.userLabTestItem.cursor">Cursor</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('cursor')} />
                </th>
                <th className="hand" onClick={sort('instructions')}>
                  <Translate contentKey="tinyMedicRestApp.userLabTestItem.instructions">Instructions</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('instructions')} />
                </th>
                <th className="hand" onClick={sort('readingType')}>
                  <Translate contentKey="tinyMedicRestApp.userLabTestItem.readingType">Reading Type</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('readingType')} />
                </th>
                <th className="hand" onClick={sort('patientType')}>
                  <Translate contentKey="tinyMedicRestApp.userLabTestItem.patientType">Patient Type</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('patientType')} />
                </th>
                <th className="hand" onClick={sort('exposed')}>
                  <Translate contentKey="tinyMedicRestApp.userLabTestItem.exposed">Exposed</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('exposed')} />
                </th>
                <th className="hand" onClick={sort('limits')}>
                  <Translate contentKey="tinyMedicRestApp.userLabTestItem.limits">Limits</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('limits')} />
                </th>
                <th className="hand" onClick={sort('statusCode')}>
                  <Translate contentKey="tinyMedicRestApp.userLabTestItem.statusCode">Status Code</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('statusCode')} />
                </th>
                <th className="hand" onClick={sort('groupPanelId')}>
                  <Translate contentKey="tinyMedicRestApp.userLabTestItem.groupPanelId">Group Panel Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('groupPanelId')} />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.userLabTestItem.glucoUser">Gluco User</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.userLabTestItem.category">Category</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.userLabTestItem.item">Item</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.userLabTestItem.labPres">Lab Pres</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.userLabTestItem.recommendedAntibiotic">Recommended Antibiotic</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {userLabTestItemList.map((userLabTestItem, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/user-lab-test-item/${userLabTestItem.id}`} color="link" size="sm">
                      {userLabTestItem.id}
                    </Button>
                  </td>
                  <td>{userLabTestItem.active ? 'true' : 'false'}</td>
                  <td>{userLabTestItem.lastupdated}</td>
                  <td>{userLabTestItem.microSec}</td>
                  <td>{userLabTestItem.result}</td>
                  <td>{userLabTestItem.timestampentry}</td>
                  <td>{userLabTestItem.observation}</td>
                  <td>{userLabTestItem.cursor}</td>
                  <td>{userLabTestItem.instructions}</td>
                  <td>{userLabTestItem.readingType}</td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.PatientType.${userLabTestItem.patientType}`} />
                  </td>
                  <td>{userLabTestItem.exposed ? 'true' : 'false'}</td>
                  <td>{userLabTestItem.limits}</td>
                  <td>{userLabTestItem.statusCode}</td>
                  <td>{userLabTestItem.groupPanelId}</td>
                  <td>
                    {userLabTestItem.glucoUser ? (
                      <Link to={`/gluco-user/${userLabTestItem.glucoUser.id}`}>{userLabTestItem.glucoUser.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {userLabTestItem.category ? (
                      <Link to={`/lab-test-category/${userLabTestItem.category.id}`}>{userLabTestItem.category.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {userLabTestItem.item ? <Link to={`/lab-test-item/${userLabTestItem.item.id}`}>{userLabTestItem.item.id}</Link> : ''}
                  </td>
                  <td>
                    {userLabTestItem.labPres ? (
                      <Link to={`/lab-prescription/${userLabTestItem.labPres.id}`}>{userLabTestItem.labPres.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {userLabTestItem.recommendedAntibiotic ? (
                      <Link to={`/antibiotic/${userLabTestItem.recommendedAntibiotic.id}`}>{userLabTestItem.recommendedAntibiotic.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/user-lab-test-item/${userLabTestItem.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/user-lab-test-item/${userLabTestItem.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/user-lab-test-item/${userLabTestItem.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="tinyMedicRestApp.userLabTestItem.home.notFound">No User Lab Test Items found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={userLabTestItemList && userLabTestItemList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default UserLabTestItem;
